/* TenzBuild product showcase: slides, option chips + colour swatches bound to real variants, 3D motion.
   One section can mix product types (laptop, monitor, drives, keyboard, mouse). */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var COL = /^(colou?r)$/i;

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function splitList(s) { return String(s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean); }
  function parsePalette(s) {
    return String(s || '').split(/\n+/).map(function (line) {
      var p = splitList(line);
      return p.length >= 2 ? { name: p[0], body: p[1], edge: p[2] || p[1] } : null;
    }).filter(Boolean);
  }
  function uniq(a) { return a.filter(function (x, i) { return a.indexOf(x) === i; }); }

  // Remove the white studio background around a product photo: flood-fill near-white pixels
  // that touch the image edge, so white parts inside the product are kept.
  var cutCache = {};
  function cutout(url) {
    if (cutCache[url]) return cutCache[url];
    cutCache[url] = new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight, scale = Math.min(1, 1400 / w);
          w = Math.round(w * scale); h = Math.round(h * scale);
          var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
          var cx = cv.getContext('2d'); cx.drawImage(img, 0, 0, w, h);
          var id = cx.getImageData(0, 0, w, h), px = id.data, n = w * h;
          var seen = new Uint8Array(n), stack = [];
          var whiteness = function (i) { var o = i * 4; return Math.min(px[o], px[o + 1], px[o + 2]); };
          for (var x = 0; x < w; x++) { stack.push(x, (h - 1) * w + x); }
          for (var y = 0; y < h; y++) { stack.push(y * w, y * w + w - 1); }
          while (stack.length) {
            var i = stack.pop();
            if (seen[i]) continue; seen[i] = 1;
            var v = whiteness(i);
            if (v < 214) continue;
            px[i * 4 + 3] = v >= 244 ? 0 : Math.round(255 * (244 - v) / 30);
            var cxi = i % w;
            if (cxi > 0) stack.push(i - 1);
            if (cxi < w - 1) stack.push(i + 1);
            if (i >= w) stack.push(i - w);
            if (i < n - w) stack.push(i + w);
          }
          cx.putImageData(id, 0, 0);
          resolve(cv.toDataURL('image/png'));
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = url;
    });
    return cutCache[url];
  }

  // WebGL laptop loader (three.js from cdnjs, then laptop-gl.js from the theme)
  var THREE_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var glPromise = null;
  function loadScript(src) {
    return new Promise(function (res, rej) { var s = document.createElement('script'); s.src = src; s.async = true; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
  }
  function hasWebGL() {
    try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl'))); } catch (e) { return false; }
  }
  function loadGL(glSrc) {
    if (glPromise) return glPromise;
    if (!hasWebGL() || !glSrc) return (glPromise = Promise.reject(new Error('no webgl')));
    glPromise = (window.THREE ? Promise.resolve() : loadScript(THREE_SRC))
      .then(function () { return window.TenzLaptopGL ? null : loadScript(glSrc); })
      .then(function () { if (!window.TenzLaptopGL) throw new Error('gl failed'); });
    return glPromise;
  }

  // Small flat drawing of each product type, used in the "Next up" thumbnail
  function mini(model) {
    var s = model.swatch(model.cols[0]), d = model.d;
    if (d.tex && d.tex.screen) return '<span class="lsx-mini" style="--sh:' + s.body + ';--shd:' + s.edge + '"><span class="lsx-mini__lid" style="display:block;padding:0;background:#0b0f17 url(' + esc(d.tex.screen) + ') center/100% 100%"></span><span class="lsx-mini__base" style="display:block"></span></span>';
    if (d.spin && d.spin.length > 1) return '<img class="lsx-mini-photo lsx-mini-spin" src="' + esc(d.spin[0]) + '" alt="">';
    if (d.photos && d.photos.length) return '<img class="lsx-mini-photo" src="' + esc(d.photos[0]) + '" alt="">';
    var vars = '--wa:' + d.wall[0] + ';--wb:' + d.wall[1] + ';--sh:' + s.body + ';--shd:' + s.edge;
    switch (d.kind) {
      case 'monitor': return '<span class="lsx-mini-mon" style="' + vars + '"><b></b><i></i><u></u></span>';
      case 'm2': return '<span class="lsx-mini-bar" style="' + vars + ';width:72px;height:18px;--r:2px;border-left:5px solid #d6b24c"></span>';
      case 'portable': return '<span class="lsx-mini-bar" style="' + vars + ';width:48px;height:34px;--r:9px"></span>';
      case 'usb': return '<span style="display:flex;align-items:center"><span style="width:12px;height:10px;background:#c9cdd4;border-radius:1px"></span><span class="lsx-mini-bar" style="' + vars + ';width:44px;height:16px;--r:4px"></span></span>';
      case 'keyboard': return '<span class="lsx-mini-bar" style="' + vars + ';width:76px;height:26px;--r:5px;background-image:repeating-linear-gradient(90deg,transparent 0 3px,rgba(255,255,255,.25) 3px 7px),linear-gradient(160deg,' + s.body + ',' + s.edge + ')"></span>';
      case 'mouse': return '<span class="lsx-mini-bar" style="' + vars + ';width:24px;height:38px;--r:12px 12px 12px 12px / 10px 10px 16px 16px"></span>';
      default: return '<span class="lsx-mini" style="' + vars + '"><span class="lsx-mini__lid" style="display:block"><span class="lsx-mini__screen" style="display:block"></span></span><span class="lsx-mini__base" style="display:block"></span></span>';
    }
  }

  function init(root) {
    if (root.__lsx) return; root.__lsx = true;
    var data; try { data = JSON.parse(root.querySelector('[data-lsx-data]').textContent); } catch (e) { return; }
    if (!data.length) return;
    var q = function (s) { return root.querySelector(s); };
    var qa = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };
    var stage = q('.lsx-stage'), device = q('[data-lsx-device]'), lid = q('[data-lsx-lid]');

    if (root.dataset.hideHeader === 'true') document.body.classList.add('lsx-header-hidden');

    // Normalise each slide into: chip values, colour values, and a variant lookup
    var models = data.map(function (d) {
      var palette = parsePalette(d.palette);
      var chipRe = new RegExp('^' + String(d.chipOption || 'Memory').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i');
      if (/^memory$/i.test(d.chipOption || '')) chipRe = /^(memory|ram)$/i;
      var chipIdx = -1, colIdx = -1;
      (d.options || []).forEach(function (o, i) { if (chipRe.test(o)) chipIdx = i; if (COL.test(o)) colIdx = i; });
      var chips, cols;
      if (d.variants && d.variants.length) {
        chips = chipIdx > -1 ? uniq(d.variants.map(function (v) { return v.options[chipIdx]; })) : [];
        cols = colIdx > -1 ? uniq(d.variants.map(function (v) { return v.options[colIdx]; })) : palette.map(function (p) { return p.name; });
      } else {
        chips = splitList(d.demoRam);
        cols = palette.map(function (p) { return p.name; });
      }
      var model = { d: d, palette: palette, chipIdx: chipIdx, colIdx: colIdx, chips: chips, cols: cols, demoPrices: splitList(d.demoPrices) };
      model.swatch = function (name) {
        var hit = palette.filter(function (p) { return p.name.toLowerCase() === String(name).toLowerCase(); })[0];
        return hit || palette[0] || { name: name, body: '#D5D9E0', edge: '#A2A8B3' };
      };
      return model;
    });

    var m = 0, r = 0, c = 0;

    function variantFor(model) {
      var vs = model.d.variants || [];
      if (!vs.length) return null;
      return vs.filter(function (v) {
        return (model.chipIdx < 0 || v.options[model.chipIdx] === model.chips[r]) && (model.colIdx < 0 || v.options[model.colIdx] === model.cols[c]);
      })[0] || vs[0];
    }

    var photos = q('[data-lsx-photos]'), hint = q('[data-lsx-hint]'), angle = 0;
    function showAngle(i) {
      var imgs = photos.querySelectorAll('img');
      if (!imgs.length) return;
      angle = ((i % imgs.length) + imgs.length) % imgs.length;
      Array.prototype.forEach.call(imgs, function (im, k) { im.classList.toggle('is-on', k === angle); });
      Array.prototype.forEach.call(photos.querySelectorAll('.lsx-angles button'), function (b, k) { b.setAttribute('aria-current', String(k === angle)); });
    }
    // 360° spin: frames of the real product turning. It auto-turns, and dragging (or moving
    // the mouse across it) turns it by hand.
    var spinOn = false, spinPos = 0, spinN = 0, spinIdle = 0, spinRaf = 0, spinLast = 0, spinTween = null, spinVisible = true;
    var SPIN_FPS = 8;
    function spinShow() { if (spinN) showAngle(Math.round(spinPos)); }
    function spinLoop(t) {
      spinRaf = 0;
      if (!spinOn || !spinVisible) return;
      var dt = spinLast ? Math.min(64, t - spinLast) : 16; spinLast = t;
      if (spinTween) {
        var k = Math.min(1, (t - spinTween.t0) / spinTween.dur), e = 1 - Math.pow(1 - k, 3);
        spinPos = spinTween.from + spinTween.by * e;
        if (k >= 1) spinTween = null;
      } else if (!reduce && t > spinIdle) {
        spinPos += dt / 1000 * SPIN_FPS;
      }
      spinShow();
      spinRaf = requestAnimationFrame(spinLoop);
    }
    function spinKick() { if (spinOn && !spinRaf) { spinLast = 0; spinRaf = requestAnimationFrame(spinLoop); } }
    function spinTurn(by, dur) { if (!spinOn) return; spinTween = { from: spinPos, by: by, t0: performance.now(), dur: dur }; spinKick(); }
    function spinHold(ms) { spinIdle = performance.now() + (ms || 2200); spinTween = null; }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { spinVisible = es[0].isIntersecting; if (spinVisible) spinKick(); }).observe(photos);
    }

    function setPhotos(d) {
      var spin = !!(d.spin && d.spin.length > 1);
      var list = spin ? d.spin : (d.photos || []);
      spinOn = spin; spinN = spin ? list.length : 0; spinPos = 0; spinTween = null;
      root.classList.toggle('lsx-photo-mode', list.length > 0);
      root.classList.toggle('lsx-spin', spin);
      if (!list.length) root.classList.remove('lsx-photo-blend');
      photos.innerHTML = list.map(function (u, i) {
        return '<img src="' + esc(u) + '" alt="' + (spin ? (i ? '' : esc(d.name) + ', turning view') : esc(d.name) + ' – view ' + (i + 1) + ' of ' + list.length) + '"' + (i && !spin ? ' loading="lazy"' : '') + (spin && i ? ' aria-hidden="true"' : '') + ' draggable="false">';
      }).join('') + (list.length > 1 && !spin ? '<div class="lsx-angles" role="group" aria-label="Choose view">' + list.map(function (u, i) {
        return '<button type="button" aria-label="View ' + (i + 1) + '" data-angle="' + i + '"></button>';
      }).join('') + '</div>' : '');
      hint.textContent = spin ? hint.dataset.drag : hint.dataset.move;
      hint.hidden = list.length < 2 || (!fine && !spin);
      showAngle(0);
      if (spin) spinKick();
      var token = (setPhotos.token = (setPhotos.token || 0) + 1);
      root.classList.remove('lsx-photo-blend');
      if (spin && !d.spinCut) return;
      Array.prototype.forEach.call(photos.querySelectorAll('img'), function (im, k) {
        cutout(list[k]).then(function (src) {
          if (token === setPhotos.token) im.src = src;
        }, function () {
          if (token !== setPhotos.token) return;
          im.classList.add('lsx-blend'); root.classList.add('lsx-photo-blend');
        });
      });
    }

    var TEX = ['screen', 'lidback', 'deck', 'under'];
    var glbox = q('[data-lsx-glbox]'), gl = null, glWanted = false, lidOpen = false;
    function setTextures(d) {
      var t = d.tex || {}, on = !!(t.screen && t.deck && (d.kind || 'laptop') === 'laptop');
      root.classList.toggle('lsx-textured', on);
      TEX.forEach(function (k) {
        if (on && t[k]) root.style.setProperty('--tex-' + k, 'url("' + t[k] + '")');
        else root.style.removeProperty('--tex-' + k);
      });
      glWanted = on;
      if (!on) { root.classList.remove('lsx-gl-on'); return false; }
      if (gl) { gl.setTextures(t); root.classList.add('lsx-gl-on'); }
      else if (glbox) {
        loadGL(root.dataset.glSrc).then(function () {
          if (gl || !glWanted) { if (gl && glWanted) { gl.setTextures(models[m].d.tex); root.classList.add('lsx-gl-on'); } return; }
          gl = window.TenzLaptopGL.create(glbox);
          if (!gl) return;
          gl.setTextures(models[m].d.tex);
          if (lidOpen) gl.openNow();
          root.classList.add('lsx-gl-on');
        }).catch(function () { /* keep the CSS laptop with photos */ });
      }
      return on;
    }

    function setKind() {
      var d = models[m].d;
      var spin = !!(d.spin && d.spin.length > 1);
      var textured = setTextures(spin ? { kind: d.kind } : d);
      setPhotos(textured ? { photos: [], name: d.name } : d);
      device.dataset.kind = d.kind || 'laptop';
      device.classList.toggle('lsx-has-mouse', d.kind === 'keyboard' && !!d.withMouse);
      var sc = d.screen || [];
      q('[data-lsx-big]').textContent = sc[0] || '';
      q('[data-lsx-unit]').textContent = sc[1] || '';
      q('[data-lsx-line]').textContent = sc[2] || '';
      qa('[data-lsx-badge]').forEach(function (b) { b.textContent = d.badge || ''; });
    }

    function paint() {
      var model = models[m], s = model.swatch(model.cols[c]);
      root.style.setProperty('--stage', model.d.stage);
      root.style.setProperty('--stage-hi', model.d.hi);
      root.style.setProperty('--shell', s.body);
      root.style.setProperty('--shell-dark', s.edge);
      root.style.setProperty('--wall-a', model.d.wall[0]);
      root.style.setProperty('--wall-b', model.d.wall[1]);

      q('[data-lsx-chiplabel]').textContent = model.d.chipLabel || '';
      q('[data-lsx-ram-wrap]').hidden = model.chips.length === 0;
      q('[data-lsx-ram]').innerHTML = model.chips.map(function (x, i) {
        return '<button type="button" class="lsx-chip" aria-pressed="' + (i === r) + '" data-ram="' + i + '">' + esc(x) + '</button>';
      }).join('');
      q('[data-lsx-col-wrap]').hidden = model.cols.length < 2;
      q('[data-lsx-cols]').innerHTML = model.cols.map(function (x, i) {
        return '<button type="button" class="lsx-swatch" style="--c:' + model.swatch(x).body + '" aria-label="' + esc(x) + '" aria-pressed="' + (i === c) + '" data-col="' + i + '"></button>';
      }).join('');
      q('[data-lsx-colname]').textContent = model.cols[c] || '';

      var v = variantFor(model), price;
      var form = q('[data-lsx-form]'), view = q('[data-lsx-view]'), add = q('[data-lsx-add]');
      if (v) {
        price = v.price;
        q('[data-lsx-variant]').value = v.id;
        form.hidden = false; view.hidden = true;
        add.disabled = !v.available; add.textContent = v.available ? 'Add to cart' : 'Sold out';
      } else {
        price = model.demoPrices[r] || model.demoPrices[0] || '';
        form.hidden = !!model.d.url; view.hidden = !model.d.url;
        if (model.d.url) view.href = model.d.url;
        add.disabled = true; add.textContent = 'Add to cart';
      }
      q('[data-lsx-price]').innerHTML = esc(price) + (price && root.dataset.vat === 'true' ? '<small>incl. VAT</small>' : '');

      q('[data-lsx-dots]').innerHTML = models.length > 1 ? models.map(function (x, i) {
        return '<button type="button" class="lsx-dot" aria-label="' + esc(x.d.name) + '" aria-current="' + (i === m) + '" data-m="' + i + '"></button>';
      }).join('') : '';
      var next = q('[data-lsx-next]');
      next.hidden = models.length < 2;
      if (models.length > 1) {
        var nx = models[(m + 1) % models.length];
        q('[data-lsx-nextname]').textContent = nx.d.name;
        q('[data-lsx-nextthumb]').innerHTML = mini(nx);
      }
    }

    function setCopy() {
      var d = models[m].d;
      q('[data-lsx-name]').textContent = d.name;
      q('[data-lsx-headline]').textContent = d.headline;
      q('[data-lsx-blurb]').textContent = d.blurb;
      q('[data-lsx-mark]').textContent = d.mark;
      var b = q('[data-lsx-btn]');
      if (b) {
        var lab = (d.btn && d.btn[0]) || b.dataset.defaultLabel || '';
        b.textContent = lab; b.hidden = !lab;
        b.href = (d.btn && d.btn[1]) || b.dataset.defaultHref || b.href;
      }
    }

    function replay(cls) {
      if (reduce) return;
      if (gl && root.classList.contains('lsx-gl-on')) { if (cls === 'lsx-is-spinning') gl.spin(); else gl.nudge(); }
      if (spinOn) { spinTurn(cls === 'lsx-is-spinning' ? spinN : Math.max(2, Math.round(spinN / 10)), cls === 'lsx-is-spinning' ? 1300 : 600); return; }
      if (root.classList.contains('lsx-photo-mode')) {
        var n = photos.querySelectorAll('img').length, k = 0;
        if (n < 2) return;
        var steps = cls === 'lsx-is-spinning' ? n : 2;
        var t = setInterval(function () { showAngle(angle + 1); if (++k >= steps) clearInterval(t); }, 110);
        return;
      }
      device.classList.remove('lsx-is-spinning', 'lsx-is-nudge');
      void device.offsetWidth;
      device.classList.add(cls);
    }
    device.addEventListener('animationend', function () { device.classList.remove('lsx-is-spinning', 'lsx-is-nudge'); });

    function closeLid() { lidOpen = false; if (lid) lid.style.setProperty('--lid', '-88deg'); device.classList.add('lsx-is-closed'); photos.classList.add('lsx-is-closed'); if (gl) gl.close(); }
    function openLid() { lidOpen = true; if (lid) lid.style.setProperty('--lid', '12deg'); device.classList.remove('lsx-is-closed'); photos.classList.remove('lsx-is-closed'); if (gl) { if (reduce) gl.openNow(); else gl.open(); } if (spinOn && !reduce) spinTurn(spinN, 1500); }

    function goTo(i) {
      i = (i + models.length) % models.length;
      if (i === m) return;
      var hasP = function (k) { var d = models[k].d; return !!((d.photos && d.photos.length) || (d.spin && d.spin.length > 1)); };
      var hasT = function (k) { return !!(models[k].d.tex && models[k].d.tex.screen); };
      var sameKind = models[i].d.kind === models[m].d.kind && !hasP(i) && !hasP(m) && hasT(i) === hasT(m);
      m = i; r = 0; c = 0;
      var copy = q('[data-lsx-copy]'), mark = q('[data-lsx-mark]');
      copy.classList.add('lsx-is-out'); mark.classList.add('lsx-is-out');
      if (sameKind || reduce) {
        replay('lsx-is-spinning'); setKind(); paint();
      } else {
        // Different product type: the old one leaves, the new one makes its entrance
        closeLid();
        setTimeout(function () { setKind(); paint(); requestAnimationFrame(function () { setTimeout(openLid, 60); }); }, 420);
      }
      setTimeout(function () { setCopy(); copy.classList.remove('lsx-is-out'); mark.classList.remove('lsx-is-out'); }, reduce ? 0 : 380);
    }

    root.addEventListener('click', function (e) {
      var a = e.target.closest('[data-angle]');
      if (a) { showAngle(+a.dataset.angle); return; }
      var t = e.target.closest('[data-ram],[data-col],[data-m],[data-lsx-next]');
      if (!t) return;
      if (t.hasAttribute('data-lsx-next')) goTo(m + 1);
      else if (t.dataset.ram) { r = +t.dataset.ram; paint(); }
      else if (t.dataset.col) { c = +t.dataset.col; paint(); replay('lsx-is-nudge'); }
      else if (t.dataset.m) goTo(+t.dataset.m);
    });

    // Laptop screen clock and date
    var clock = q('[data-lsx-clock]'), date = q('[data-lsx-date]');
    function tick() {
      var d = new Date();
      clock.textContent = d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false });
      date.textContent = d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    tick(); setInterval(tick, 30000);

    setKind(); paint(); setCopy();

    // Optional auto-advance; stops for good once the shopper interacts
    var every = +root.dataset.autoplay || 0, stopped = false;
    if (every > 0 && models.length > 1 && !reduce) {
      var stop = function () { stopped = true; };
      root.addEventListener('pointerdown', stop);
      root.addEventListener('keydown', stop);
      setInterval(function () {
        if (stopped || document.hidden) return;
        var r0 = root.getBoundingClientRect();
        if (r0.bottom < 0 || r0.top > window.innerHeight) return;
        goTo(m + 1);
      }, every * 1000);
    }

    // First entrance when the section comes into view
    if (reduce || !('IntersectionObserver' in window)) { openLid(); }
    else {
      closeLid();
      var io = new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { io.disconnect(); setTimeout(openLid, 300); }
      }, { threshold: 0.3 });
      io.observe(stage);
    }

    // Photo mode: moving across the photo turns it; swiping works on touch screens
    var dragX = null, hoverX = null;
    photos.addEventListener('pointerdown', function (e) {
      if (!spinOn) return;
      dragX = e.clientX; spinHold(); hint.hidden = true;
      try { photos.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    });
    photos.addEventListener('pointerup', function () { dragX = null; if (spinOn) spinHold(1600); });
    photos.addEventListener('pointercancel', function () { dragX = null; });
    photos.addEventListener('pointerleave', function () { hoverX = null; });
    photos.addEventListener('pointermove', function (e) {
      if (spinOn) {
        var w = photos.getBoundingClientRect().width || 1;
        var ref = dragX !== null ? dragX : (e.pointerType === 'mouse' ? hoverX : null);
        if (ref !== null) { spinPos -= (e.clientX - ref) / w * spinN * (dragX !== null ? 1 : 0.6); spinHold(dragX !== null ? 2200 : 1400); spinShow(); }
        if (dragX !== null) dragX = e.clientX; else if (e.pointerType === 'mouse') hoverX = e.clientX;
        if (dragX !== null || ref !== null) hint.hidden = true;
        return;
      }
      if (e.pointerType !== 'mouse') return;
      var n = photos.querySelectorAll('img').length; if (n < 2) return;
      var b = photos.getBoundingClientRect();
      showAngle(Math.min(n - 1, Math.max(0, Math.floor((e.clientX - b.left) / b.width * n))));
      hint.hidden = true;
    });
    var sx = null;
    photos.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
    photos.addEventListener('touchend', function (e) {
      if (sx === null || spinOn) return; var dx = e.changedTouches[0].clientX - sx; sx = null;
      if (Math.abs(dx) > 30) showAngle(angle + (dx < 0 ? 1 : -1));
    });

    // The product turns toward the pointer
    if (!reduce && fine) {
      stage.addEventListener('pointermove', function (e) {
        var b = stage.getBoundingClientRect();
        var lx = (e.clientX - b.left) / b.width - 0.5, ly = 0.5 - (e.clientY - b.top) / b.height;
        device.style.setProperty('--ty', (lx * 26).toFixed(1) + 'deg');
        device.style.setProperty('--lx', (lx * 2).toFixed(3));
        if (gl) gl.point(lx * 30, ly * 10);
        device.style.setProperty('--tx', ((0.5 - (e.clientY - b.top) / b.height) * 12).toFixed(1) + 'deg');
      });
      stage.addEventListener('pointerleave', function () {
        device.style.setProperty('--ty', '0deg'); device.style.setProperty('--tx', '0deg'); device.style.setProperty('--lx', '0');
        if (gl) gl.point(0, 0);
      });
    }
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-lsx]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var el = e.target.querySelector('[data-lsx]'); if (el) init(el); });
  document.addEventListener('shopify:section:unload', function (e) { if (e.target.querySelector('[data-lsx]')) document.body.classList.remove('lsx-header-hidden'); });
})();
